import {StatusMessage} from "../model/status-message";
import {StatusMessageType} from "../model/enums/status-message-type";

export default class StatusMessageUtils{

  /**
   * Returns the text associated with a given status message.
   *
   * Uses either the message of the given status message or generates a text from the type of the status message
   *
   * @param statusMessage Status message for which the text should be retrieved
   * @return Message of the status message if its set else the generated text depending on the status message type
   */
  public static getStatusMessageText(statusMessage: StatusMessage): string{

    // Directly return the message if its set.
    if(statusMessage.message){
      return statusMessage.message
    }

    // Return a text based on the message type of the given status message
    switch (statusMessage.type) {
      case StatusMessageType.CUSTOMER_INTERACTION_OPENED:
        return "Eine Handlungsaufforderung wurde erstellt. Sollten Sie bereits reagiert haben kümmern wir uns schnellstmöglich um die weitere Bearbeitung.";
      case StatusMessageType.CUSTOMER_INTERACTION_ADMIN_RESPONSE:
        return "Wir haben weitere Informationen nachgefordert.";
      case StatusMessageType.CUSTOMER_INTERACTION_USER_RESPONSE:
        return "Neue Informationen wurden durch den Kunden bereitgestellt.";
      case StatusMessageType.CUSTOMER_INTERACTION_CLOSED:
        return "Die fehlenden Informationen wurden bereitgestellt. Die Handlungsaufforderung wurde geschlossen.";
      case StatusMessageType.DOCUMENT_COMPLETED:
        return "Die Auskunft ist eingetroffen.";
      case StatusMessageType.AUTOMATIC_REMINDER:
        return "Die Behörde hat noch nicht reagiert. Eine schriftliche Erinnerung wurde versendet.";
      case StatusMessageType.DOCUMENT_CANCELED:
        return "Die Auskunft wurde storniert.";
      case StatusMessageType.DOCUMENT_REQUESTED:
        return "Die Anfrage wurden an die Behörde übermittelt.";
      case StatusMessageType.PHONE_TICKET_CREATED:
        return "Die Behörde wird telefonisch kontaktiert";
    }
  }

  /**
   * Retrieves the image URL for the latest status message based on its type.
   * If the last status message event is after the 'DOCUMENT_COMPLETED' status message event the DOCUMENT_COMPLETED Icon is returned.
   *
   * @param isLoadingStatusMessages Boolean which determines whether the status message are still loading
   * @param statusMessages Array of status messages which are used to retrieve the image URL
   * @return URL of the image corresponding to the latest status message type
   */
  public static getImageForStatusMessage(isLoadingStatusMessages: boolean, statusMessages: StatusMessage[]): string {
    if (!isLoadingStatusMessages && statusMessages.length > 0) {

      const documentCompletedStatusMessage: StatusMessage | undefined = statusMessages.find(statusMessage => statusMessage.type === StatusMessageType.DOCUMENT_COMPLETED);
      const latestStatusMessage: StatusMessage = statusMessages[statusMessages.length - 1];

      if (documentCompletedStatusMessage) {
        const isLatestStatusMessageDocumentCompletedStatus = documentCompletedStatusMessage.id === latestStatusMessage.id;
        const isLatestStatusMessageAfterDocumentCompletedStatus = documentCompletedStatusMessage.date.getTime() < latestStatusMessage.date.getTime();

        //If the last status message is either the 'DOCUMENT_COMPLETED' status message or is after the 'DOCUMENT_COMPLETED' status message return the information received icon
        if (isLatestStatusMessageDocumentCompletedStatus || isLatestStatusMessageAfterDocumentCompletedStatus) {
          return "assets/images/status-messages/information_received_icon.svg";
        }
      }
      switch (latestStatusMessage.type) {
        case StatusMessageType.DOCUMENT_CANCELED:
          return "assets/images/status-messages/order_canceled_icon.svg";
        case StatusMessageType.CUSTOMER_INTERACTION_OPENED:
          return "assets/images/status-messages/call_to_action_icon.svg";
        default:
          return "assets/images/status-messages/information_in_progress_icon.svg";
      }
    }
  }

  /**
   * Determines if the latest status message of the given array indicates the completion of a document.
   * A StatusMessage is considered a completion status message if its type is either "DOCUMENT_COMPLETED" or "DOCUMENT_CANCELED".
   * If the latest status message is not a typical status message with a completion status but it comes after the 'DOCUMENT_COMPLETED status message
   * it is also considered a status message representing a completion status
   *
   * @param isLoadingStatusMessages Boolean which determines whether the status message are still loading
   * @param statusMessages Array of status messages which are used to determine if the latest status message indicates the completion of a document
   * @return True if if the latest status message indicates the completion of a document else false
   */
  public static isLatestStatusMessageCompletionStatus(isLoadingStatusMessages: boolean, statusMessages: StatusMessage[]): boolean {
    if (isLoadingStatusMessages || statusMessages.length === 0) {
      return false;
    }

    const documentCompletedStatusMessage: StatusMessage | undefined = statusMessages.find(statusMessage => statusMessage.type === StatusMessageType.DOCUMENT_COMPLETED);
    const latestStatusMessage = statusMessages[statusMessages.length - 1];

    if(documentCompletedStatusMessage && documentCompletedStatusMessage.date.getTime() < latestStatusMessage.date.getTime()){
      return true;
    }

    return latestStatusMessage.type === StatusMessageType.DOCUMENT_COMPLETED ||
        latestStatusMessage.type === StatusMessageType.DOCUMENT_CANCELED;
  }

  /**
   * Determines if the latest status message of the given array indicates that a customer interaction was opened for the user.
   *
   * @param isLoadingStatusMessages Boolean which determines whether the status messages are still loading
   * @param statusMessages Array of status messages which are used to determine if the latest status message indicates that a customer interaction was opened for the user.
   * @return True if the latest status message indicates that a customer interaction was opened for the user.
   */
  public static isLatestStatusMessageCustomerInteractionStatus(isLoadingStatusMessages: boolean, statusMessages: StatusMessage[]): boolean{
    if (isLoadingStatusMessages || statusMessages.length === 0) {
      return false;
    }
    const latestStatusMessage = statusMessages[statusMessages.length - 1];
    return latestStatusMessage.type === StatusMessageType.CUSTOMER_INTERACTION_OPENED;
  }


}
